import React, { useState } from 'react';
import { Upload, Zap, Brain, Loader2 } from 'lucide-react';
import DataUpload from '@components/DataUpload';
import GatingVisualization from '@components/GatingVisualization';
import AnalysisResults from '@components/AnalysisResults';
import FarOutDiscoveries from '@components/FarOutDiscoveries';
import { API_URL } from './config';

function App() {
  const [step, setStep] = useState('upload'); // upload | analyzing | results
  const [data, setData] = useState(null);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(0);

  const handleAnalyze = async (quantumData) => {
    setStep('analyzing');
    setProgress(0);
    try {
      // Phase 1: Upload data
      setProgress(10);

      // Phase 2: Run analysis on worker
      const apiUrl = `${API_URL}/api/analyze`;
      console.log('Fetching from:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          trajectories: quantumData.trajectories,
          config: {
            dim: 128,
            kfrac: 0.5,
            ttfs: {
              tWindowMs: 100,
              tResolutionUs: 1,
              coincidenceTauUs: 10
            }
          }
        })
      });

      setProgress(30);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Analysis failed');
      }

      setProgress(100);
      setResults(result);
      setStep('results');
    } catch (error) {
      console.error('Analysis failed:', error);
      console.error('API_URL:', API_URL);
      console.error('Full error:', error);
      alert(`Analysis failed: ${error.message}\n\nCheck browser console for details.`);
      setStep('upload');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white">
      {/* Header */}
      <header className="border-b border-purple-500/30 bg-black/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Brain className="w-10 h-10 text-purple-400" />
            <div>
              <h1 className="text-3xl font-bold">Quantum Collapse Analyzer</h1>
              <p className="text-gray-400">
                Hunting for retrocausality, consciousness effects, and simulation glitches
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {step === 'upload' && (
          <DataUpload onAnalyze={handleAnalyze} />
        )}

        {step === 'analyzing' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-800/50 backdrop-blur rounded-lg p-8 text-center">
              <Loader2 className="w-16 h-16 text-purple-400 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Analyzing Quantum Jumps...</h2>
              <p className="text-gray-400 mb-6">
                Running USAD gating, spike-only analysis, and 12 far-out phenomena tests
              </p>
              
              <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-400 mt-2">{progress}%</p>
            </div>
          </div>
        )}

        {step === 'results' && results && (
          <div className="space-y-8">
            <GatingVisualization gating={results.gating} />
            <AnalysisResults analysis={results.analysis} />
            <FarOutDiscoveries farOut={results.far_out} />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-purple-500/30 bg-black/50 backdrop-blur-sm mt-16">
        <div className="container mx-auto px-4 py-6 text-center text-gray-400">
          <p>
            <strong>Warning:</strong> This tool looks for paradigm-shifting physics.
            Triple-check any Tier 3-4 findings before calling Elon Musk.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;

